import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { money, when } from "@/lib/rides";
import { PageHead } from "@/pages/admin/AdminLayout";
import { Ledger, TX_LABEL } from "@/components/CreditWallet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const Stat = ({ label, value, testid }) => (
  <div className="rounded-xl border bg-card p-3"><p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</p><p className="font-display text-xl font-bold" data-testid={testid}>{money(value)}</p></div>
);
const run = async (fn, msg, after) => {
  try { await fn(); toast.success(msg); after(); } catch (e) { toast.error(formatApiError(e)); }
};

function Purchases({ refresh }) {
  const { data } = useQuery({ queryKey: ["creditPurchases"], queryFn: () => api.get("/admin/credit/purchases").then((r) => r.data) });
  if (!data?.length) return null;
  return (
    <section className="space-y-2 rounded-xl border border-amber-400/40 bg-card p-4" data-testid="credit-purchases">
      <h2 className="font-semibold">Purchases waiting for payment confirmation</h2>
      {data.map((p) => (
        <div key={p.id} className="flex flex-wrap items-center justify-between gap-2 text-sm" data-testid={`credit-purchase-${p.id}`}>
          <span>{p.rider_name} · {money(p.amount)} · {when(p.created_at)}</span>
          <span className="flex gap-1.5">
            <Button size="sm" onClick={() => { const ref = window.prompt("Payment reference (receipt #, 'cash', last 4…)"); if (ref) run(() => api.post(`/admin/credit/purchases/${p.id}/confirm`, { payment_reference: ref }), "Credit added", refresh); }} data-testid={`credit-purchase-confirm-${p.id}`}>Confirm paid</Button>
            <Button size="sm" variant="ghost" onClick={() => run(() => api.post(`/admin/credit/purchases/${p.id}/fail`), "Marked failed — no credit added", refresh)} data-testid={`credit-purchase-fail-${p.id}`}>Payment failed</Button>
          </span>
        </div>
      ))}
    </section>
  );
}

function RiderWallet({ rider, refresh }) {
  const { data: w, refetch } = useQuery({ queryKey: ["adminWallet", rider.id], queryFn: () => api.get(`/admin/credit/riders/${rider.id}`).then((r) => r.data) });
  const [f, setF] = useState({ action: "add_promotional", amount: "", reason: "", payment_reference: "", expires_at: "" });
  const done = () => { refetch(); refresh(); setF({ ...f, amount: "", reason: "" }); };
  const submit = () => run(() => api.post(`/admin/credit/riders/${rider.id}`, { ...f, amount: Number(f.amount), expires_at: f.expires_at ? new Date(f.expires_at).toISOString() : null,
    payment_reference: f.payment_reference || null, reason: f.reason || null }), "Ride Credit updated", done);
  if (!w) return null;
  return (
    <section className="space-y-4 rounded-xl border bg-card p-4" data-testid="admin-rider-wallet">
      <h2 className="font-semibold">{w.name} <span className="text-sm font-normal text-muted-foreground">{w.email} · Post-Payment: {w.post_payment_status || "none"}</span></h2>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
        <Stat label="Balance" value={w.balance} testid="wallet-balance" /><Stat label="Reserved" value={w.reserved} testid="wallet-reserved" />
        <Stat label="Purchased" value={w.purchased} testid="wallet-purchased" /><Stat label="Used" value={w.used} testid="wallet-used" /><Stat label="Refunded" value={w.refunded} testid="wallet-refunded" />
      </div>
      <div className="grid gap-2 sm:grid-cols-5">
        <select value={f.action} onChange={(e) => setF({ ...f, action: e.target.value })} className="h-9 rounded-md border bg-background px-2 text-sm" data-testid="wallet-action-select">
          <option value="add_promotional">Add promotional credit</option><option value="add_purchased">Record purchased credit</option>
          <option value="adjust">Adjust (+/-)</option><option value="refund">Refund to credit</option>
        </select>
        <Input type="number" step="0.01" placeholder="Amount" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} data-testid="wallet-amount-input" />
        <Input placeholder="Reason (optional)" value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} data-testid="wallet-reason-input" />
        {f.action === "add_promotional" ? <Input type="datetime-local" value={f.expires_at} onChange={(e) => setF({ ...f, expires_at: e.target.value })} className="[color-scheme:dark]" title="Optional expiry" data-testid="wallet-expires-input" />
          : <Input placeholder="Payment reference" value={f.payment_reference} onChange={(e) => setF({ ...f, payment_reference: e.target.value })} data-testid="wallet-ref-input" />}
        <Button onClick={submit} disabled={!Number(f.amount)} data-testid="wallet-submit-button">Apply</Button>
      </div>
      {w.lots.length > 0 && (
        <div className="space-y-1 text-xs" data-testid="wallet-lots">
          <p className="font-semibold">Open credit</p>
          {w.lots.map((l) => (
            <div key={l.id} className="flex items-center justify-between">
              <span>{l.source} · {money(l.remaining)} of {money(l.amount)}{l.expires_at ? ` · expires ${when(l.expires_at)}` : ""}</span>
              <Button size="sm" variant="ghost" className="h-7 text-destructive" onClick={() => run(() => api.post(`/admin/credit/lots/${l.id}/void`, { reason: window.prompt("Reason (optional)") || null }), "Credit voided", done)} data-testid={`wallet-void-${l.id}`}>Void</Button>
            </div>
          ))}
        </div>
      )}
      <Ledger rows={w.ledger} />
    </section>
  );
}

function Report({ s }) {
  return (
    <div className="grid grid-cols-2 gap-2 md:grid-cols-4" data-testid="credit-report">
      <Stat label="Outstanding rider credit" value={s.credit.outstanding} testid="report-outstanding" />
      <Stat label="Reserved for rides" value={s.credit.reserved} testid="report-reserved" />
      <Stat label="Purchased (all time)" value={s.credit.purchased} testid="report-purchased" />
      <Stat label="Promotional (all time)" value={s.credit.promotional} testid="report-promotional" />
      <Stat label="Used" value={s.credit.used} testid="report-used" /><Stat label="Refunded" value={s.credit.refunded} testid="report-refunded" />
      <Stat label="Adjustments" value={s.credit.adjustments} testid="report-adjustments" />
      <Stat label="Post-payment outstanding" value={s.post_payment.due.amount + s.post_payment.overdue.amount} testid="report-post-payment" />
    </div>
  );
}

export default function AdminCredit() {
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const [rider, setRider] = useState(null);
  const [type, setType] = useState("");
  const { data: s } = useQuery({ queryKey: ["billingSummary"], queryFn: () => api.get("/admin/billing/summary").then((r) => r.data) });
  const { data: users } = useQuery({ queryKey: ["users", q], queryFn: () => api.get("/admin/users", { params: { q } }).then((r) => r.data), enabled: q.length > 1 });
  const { data: ledger } = useQuery({ queryKey: ["creditLedger", type], queryFn: () => api.get("/admin/credit/ledger", { params: { transaction_type: type || undefined } }).then((r) => r.data) });
  const refresh = () => ["billingSummary", "creditPurchases", "creditLedger"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
  return (
    <div className="space-y-5">
      <PageHead title="Ride Credit" sub="Prepaid value on rider accounts. Every change is a ledger entry — balances can't be edited directly." />
      {s && <Report s={s} />}
      <Purchases refresh={refresh} />
      <div className="space-y-2">
        <Input placeholder="Search rider by name or email" value={q} onChange={(e) => setQ(e.target.value)} data-testid="credit-rider-search" />
        {q.length > 1 && users?.slice(0, 6).map((u) => (
          <button key={u.id} type="button" onClick={() => { setRider(u); setQ(""); }} className="block w-full rounded-lg px-2 py-1.5 text-left text-sm hover:bg-muted" data-testid="credit-rider-result">
            {u.name} · {u.email} · {money(u.credit_balance || 0)}
          </button>
        ))}
      </div>
      {rider && <RiderWallet key={rider.id} rider={rider} refresh={refresh} />}
      <section className="space-y-2 rounded-xl border bg-card p-4">
        <div className="flex items-center justify-between gap-2">
          <h2 className="font-semibold">All transactions</h2>
          <select value={type} onChange={(e) => setType(e.target.value)} className="h-8 rounded-md border bg-background px-2 text-xs" data-testid="ledger-type-filter">
            <option value="">All types</option>{Object.entries(TX_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </div>
        <Ledger rows={ledger?.map((e) => ({ ...e, description: `${e.rider_name} · ${e.description}` }))} />
      </section>
    </div>
  );
}
